import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { getFunctions, httpsCallable } from "firebase/functions";

const UserRegistration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [inviteId, setInviteId] = useState("");
  const [inviteData, setInviteData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const functions = getFunctions();
  const validateInvite = httpsCallable(functions, "validateInvite");
  const createValidatedUserAccount = httpsCallable(
    functions,
    "createValidatedUserAccount"
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const id = urlParams.get("invite");

    if (!id) {
      setError(
        "Invalid invitation link. Please check your email for the correct link."
      );
      setLoading(false);
      return;
    }

    setInviteId(id);
    loadInviteData(id);
  }, [location]);

  const loadInviteData = async (id) => {
    try {
      setLoading(true);
      const result = await validateInvite({ inviteId: id });

      if (result.data.success) {
        setInviteData(result.data.invite);
        setError("");
      } else {
        setError(result.data.error || "Invalid or expired invitation.");
      }
    } catch (err) {
      console.error("Error validating invite:", err);
      setError("Failed to validate invitation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");

    try {
      const result = await createValidatedUserAccount({
        inviteId,
        password,
        confirmPassword,
      });

      if (result.data.success) {
        setMessage(
          "Account created successfully! You can now log in with your credentials."
        );
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(
          result.data.error || "Failed to create account. Please try again."
        );
      }
    } catch (err) {
      console.error("Error creating account:", err);
      setError("Failed to create account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        // bgcolor="grey.100"
      >
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Validating invitation...</Typography>
        </Paper>
      </Box>
    );
  }

  if (error && !inviteData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        // bgcolor="grey.100"
      >
        <Paper sx={{ p: 4, maxWidth: 400 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" onClick={() => navigate("/")} fullWidth>
            Return to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      // bgcolor="grey.100"
      p={2}
    >
      <Paper sx={{ p: 4, maxWidth: 500, width: "100%" }}>
        <Typography variant="h4" gutterBottom textAlign="center">
          Complete Your Registration
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3 }}
          textAlign="center"
        >
          You've been invited to join the RoomRoster Ecosystem. Please confirm
          your information and set a password.
        </Typography>

        {inviteData && (
          <Box sx={{ mb: 3, p: 2, borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Your Information:
            </Typography>
            <Typography>
              <strong>Name:</strong> {inviteData.firstName}{" "}
              {inviteData.lastName}
            </Typography>
            <Typography>
              <strong>Email:</strong> {inviteData.email}
            </Typography>
            <Typography>
              <strong>Username:</strong> {inviteData.userName}
            </Typography>
            <Typography>
              <strong>Role:</strong> {inviteData.userType}
            </Typography>
            <Typography>
              <strong>Properties:</strong>{" "}
              {inviteData.properties
                ?.map((p) => p.displayName || p.id)
                .join(", ")}
            </Typography>
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            helperText="Password must be at least 6 characters long"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            type={showConfirmPassword ? "text" : "password"}
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{ mt: 3 }}
          >
            {submitting ? "Creating Account..." : "Create Account"}
          </Button>
        </form>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button variant="text" onClick={() => navigate("/")} size="small">
            Return to Login
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserRegistration;
